import React, { useState, useEffect, useRef } from "react";
import { SearchSelect } from "@mtm/components";
import {
  Row,
  Col,
  Image,
  Input,
  Tooltip,
  notification,
  Typography,
} from "antd";
import TripAddressStyle from "./TripAddress.module.scss";
import Style from "../../../SomeoneElseWillUseServices/SomeoneElseWillUseServices.module.scss"
import {
  getAutoComplete,
  getPlaceDetails,
  getLookupListByListName,
  validateAddressUSPS,
} from "../../../../services";
import {
  LookupItem,
  TripAddressDetails,
} from "../../../../types/ApplicationTypes";
import DriverInstruction from "../../../../assets/icons/driver_instruction.svg";
import {
  AddressType,
  TTripAddress,
  TripBothAddress,
  TDriverInstruction,
} from "../../../../types/TripTypes";
import HomeIcon from "../../../../assets/icons/trip_address.svg";
import { useWindowSize } from "../../../../hooks/useWindowSize";
import {
  GeneralDefinition,
  InputBoxLength,
} from "../../../../types/ApplicationTypes";
import { CaretDownOutlined } from "@ant-design/icons";
import { useTripsCtx } from "../../../../context/TripDetailsContext";

const defaultDriverInstructionValue: TDriverInstruction = {
  pickup: false,
  dropOff: false,
};

type Props = {
  address: TripBothAddress;
  handleUpdate: (address: TripBothAddress) => void;
  handleClear: (field: string) => void;
  changeOtherFields: (name: string, value: string, field: string) => void;
  clearinstructionField: (field: string) => void;
  checkDriverInstrction: (field: string, value: boolean) => void;
  checkWandaService: (zipCode: string, addressType: string) => void;
};

const AddressDisplay = (address: TTripAddress): string => {
  const { address1, address2, city, state, zip } = address;
  const finalAddress =
    address1 && address2
      ? [`${address1} ${address2}`, city, state, zip]
      : [address1, city, state, zip];
  const fullAddress = finalAddress.filter(Boolean).join(", ");

  return fullAddress || "";
};

const TripAddress: React.FC<Props> = ({
  address,
  handleUpdate,
  handleClear,
  changeOtherFields,
  clearinstructionField,
  checkDriverInstrction,
  checkWandaService,
}) => {
  const { width } = useWindowSize();
  const [sugg, setSugg] = useState({ pickup: [], dropOff: [] });
  const [states, setStates] = useState<LookupItem[]>([]);
  const { uspsFieldValidationErrorCtx } = useTripsCtx();
  const [detailedViewAddress, setDetailedViewAddress] = useState<
    TDriverInstruction
  >(defaultDriverInstructionValue);
  const [driverInstruction, setDriverInstruction] = useState<
    TDriverInstruction
  >(defaultDriverInstructionValue);
  const inputPickupRefs = useRef<{ [key: string]: Input | null }>({
    pickup: null,
    dropOff: null,
  });
  const [
    uspsFieldValidationError,
    setUspsFieldValidationError,
  ] = uspsFieldValidationErrorCtx;
  let timer: NodeJS.Timeout;

  const handlePlaceAutoComplete = async (
    event: React.ChangeEvent<HTMLInputElement>,
    field: string
  ) => {
    if (timer) {
      clearTimeout(timer);
    }

    const { value } = event.target;
    const addressStatus: TDriverInstruction = detailedViewAddress;
    const isUspsSearch =
      addressStatus[field as keyof TDriverInstruction] && value.length >= 3;
    const existingAddress: TripBothAddress = address;

    let addressSelected: TTripAddress | string | any = isUspsSearch
      ? {
          ...existingAddress[field as keyof TripBothAddress],
          address1: value,
        }
      : {
          address1: value,
        };

    setUspsFieldValidationError(defaultDriverInstructionValue);
    if (
      event.nativeEvent instanceof KeyboardEvent &&
      event.nativeEvent.key === "Backspace" &&
      value === ""
    ) {
      if (inputPickupRefs.current[field]) {
        inputPickupRefs.current[field]?.focus();
      }
    }
    timer = setTimeout(async () => {
      if (
        value.length >= 3 &&
        !addressStatus[field as keyof TDriverInstruction]
      ) {
        try {
          const result = await getAutoComplete(value);
          if (result.predictions) {
            setSugg({ ...sugg, [field]: result.predictions });
          }
        } catch (error) {
          notification.error({
            message: "Error",
            description: "Failed to get auto complete address.",
          });
        }
      } else if (isUspsSearch) {
        try {
          const uspsAddressResponse = await validateAddressUSPS(
            addressSelected
          );
          if (
            uspsAddressResponse &&
            Object.keys(uspsAddressResponse).length > 0
          ) {
            for (const key in uspsAddressResponse) {
              if (
                Object.prototype.hasOwnProperty.call(uspsAddressResponse, key)
              ) {
                const value = uspsAddressResponse[key];
                if (key !== "address1") addressSelected[key] = value;
              }
            }
            handleUpdate({ [field]: addressSelected });
          } else {
            setUspsFieldValidationError((prev: TDriverInstruction) => ({
              ...prev,
              [field]: true,
            }));
          }
        } catch (error) {
          notification.error({
            message: "Error",
            description: error ,
          });
        }
      } else {
        setSugg({ ...sugg, [field]: [] });
      }
    }, 500);
    handleUpdate({ [field]: addressSelected });
  };

  const handlePlaceSelected = async (selected: any, field: string) => {
    let addressSelected: TTripAddress = {
      address1: "",
      address2: "",
      city: "",
      state: "",
      zip: "",
      county: "",
      latitude: "",
      longitude: "",
    };
    const results = await getPlaceDetails(
      selected.place_id,
      selected.description,
    );
    if (results.results) {
      addressSelected.latitude = results.results[0].lat;
      addressSelected.longitude = results.results[0].lng;
      const placeItems = results.results[0].address_components;

      placeItems.forEach((component: any) => {
        const componentType = component.types[0];

        if (componentType === "street_number") {
          addressSelected.address1 = component.short_name;
        } else if (componentType === "route") {
          const routeStr = `${addressSelected.address1} ${component.short_name}`;
          addressSelected.address1 = routeStr;
        } else if (componentType === "locality") {
          addressSelected.city = component.short_name;
        } else if (componentType === "administrative_area_level_1") {
          addressSelected.state = component.short_name;
        } else if (componentType === "postal_code") {
          addressSelected.zip = component.long_name;
        } else if (componentType === "administrative_area_level_2") {
          addressSelected.county = component.long_name;
        }
      });
      addressSelected.strAddress = AddressDisplay(addressSelected);
      handleUpdate({ [field]: addressSelected });
      setSugg({ ...sugg, [field]: [] });
      checkWandaService(String(addressSelected.zip), field);
    }
  };

  const changeFields = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: string
  ) => {
    const { name, value } = event.target;
    if (value.length <= InputBoxLength.DriverInstructionText) {
      if (name === TripAddressDetails.DriverInstructions) {
        const instructions = value.replace(/\s\s+/g, " ");
        instructions.trimStart() === instructions &&
          changeOtherFields(name, instructions, field);
      } else {
        changeOtherFields(name, value, field);
      }
    }
  };

  const handleDefaultAddressDetailView = (field: string, newValue: boolean) => {
    setDetailedViewAddress((prevView) => ({
      ...prevView,
      [field]: newValue,
    }));
  };

  const handleClearAddressAndRest = (field: string, newValue: boolean) => {
    handleClear(field);
    if (!newValue) {
      setDetailedViewAddress((prevView) => ({
        ...prevView,
        [field]: newValue,
      }));
    }
  };

  const handleInstructionView = (
    field: string,
    newValue: boolean,
    isRemove?: boolean
  ) => {
    setDriverInstruction((preDriverInstruction) => ({
      ...preDriverInstruction,
      [field]: newValue,
    }));
    checkDriverInstrction(field, newValue);
    if (isRemove) {
      clearinstructionField(field);
    }
  };

  useEffect(() => {
    getstatelist();
  }, []);

  const getstatelist = async () => {
    let states = await getLookupListByListName(1, "StateAbbreviations");
    if (states === "") {
      states = ["a", "b"];
    }
    setStates(states);
  };

  return width > GeneralDefinition.MobileView ? (
    <>
      <Row className={TripAddressStyle.addressSection}>
        <Col xl={{ span: 19 }} lg={{ span: 18 }}>
          <Row>
            <Col xl={{ span: 9 }} lg={{ span: 10 }}>
              <label className={TripAddressStyle.titleTextBoldBlue}>
                Pickup Address
              </label>
            </Col>
            {!driverInstruction[AddressType.Pickup] && (
              <Col xl={{ push: 6 }} lg={{ push: 3 }}>
                <span
                  onClick={() =>
                    handleInstructionView(
                      AddressType.Pickup,
                      !driverInstruction[AddressType.Pickup]
                    )
                  }
                  className={TripAddressStyle.contentTextBlueDimLight}
                >
                  Add driver instructions
                </span>
              </Col>
            )}
          </Row>
          <Row align="middle">
            <Col span={21}>
              <Input
                size="large"
                autoComplete="off"
                className={TripAddressStyle.customInput}
                name={`${AddressType.Pickup}.address1`}
                onChange={(e) => handlePlaceAutoComplete(e, AddressType.Pickup)}
                value={
                  address.pickup !== undefined
                    ? detailedViewAddress[AddressType.Pickup]
                      ? address.pickup.address1
                      : AddressDisplay(address.pickup)
                    : undefined
                }
                placeholder="Enter an address"
                prefix={
                  address.pickup !== undefined &&
                  address.pickup.isHomeAddress && (
                    <Image preview={false} src={HomeIcon} width={"15px"} />
                  )
                }
                suffix={
                  <CaretDownOutlined translate={""} style={{ color: "#FFF" }} />
                }
                ref={(input) =>
                  (inputPickupRefs.current[AddressType.Pickup] = input)
                }
              />
            </Col>
            {address[AddressType.Pickup]?.zip !== undefined && (
              <Col>
                <span
                  className={TripAddressStyle.contentTextBlueDimLightwithPl}
                  onClick={() =>
                    handleClearAddressAndRest(
                      AddressType.Pickup,
                      !detailedViewAddress[AddressType.Pickup]
                    )
                  }
                >
                  Clear
                </span>
              </Col>
            )}
          </Row>
          <Row>
            <Col>
              {sugg[AddressType.Pickup].length > 0 &&
                sugg[AddressType.Pickup].map((sug: any, i: number) => (
                  <span
                    key={i}
                    className={TripAddressStyle.makeClickable}
                    onClick={() =>
                      handlePlaceSelected(sug, AddressType.Pickup)
                    }
                  >
                    {sug.description}
                  </span>
                ))}
            </Col>
          </Row>
          {!detailedViewAddress.pickup && (
            <Row className={TripAddressStyle.pickupAddressLookupSection}>
              <Col>
                <span
                  onClick={() =>
                    handleDefaultAddressDetailView(
                      AddressType.Pickup,
                      !detailedViewAddress[AddressType.Pickup]
                    )
                  }
                  className={TripAddressStyle.contentTextBlueDimLight}
                >
                  Address lookup not working?
                </span>
              </Col>
            </Row>
          )}

          {detailedViewAddress[AddressType.Pickup] && (
            <>
              {" "}
              <Row className={TripAddressStyle.addressLookupSection}>
                <Col span={21}>
                  <Input
                    size="large"
                    type="text"
                    placeholder="Address 2"
                    name="address2"
                    onChange={(e) => changeFields(e, AddressType.Pickup)}
                    value={address.pickup ? address.pickup.address2 : ""}
                    className={TripAddressStyle.customInput}
                  />
                </Col>
              </Row>
              <Row className={TripAddressStyle.addressLookupSection}>
                <Col span={8}>
                  <Input
                    size="large"
                    type="text"
                    placeholder="City"
                    name="city"
                    onChange={(e) => changeFields(e, AddressType.Pickup)}
                    value={address.pickup ? address.pickup.city : ""}                  
                    className={`${TripAddressStyle.customInput} ${uspsFieldValidationError.pickup && Style.errorBorder}`}
                  />
                </Col>
                <Col span={4} offset={1}>
                  <SearchSelect
                    placeholder="State"
                    options={states.map((r: any) => ({
                      key: r.id,
                      id: r.itemName,
                      display: r.itemName,
                    }))}
                    value={address.pickup ? address.pickup.state : ""}
                    className={`${TripAddressStyle.searchSelectSize} ${uspsFieldValidationError.pickup && TripAddressStyle.errorOutline}`}
                  />
                </Col>
                <Col span={7} offset={1}>
                  <Input
                    size="large"
                    type="text"
                    placeholder="Zip Code"
                    name="zip"
                    onChange={(e) => changeFields(e, AddressType.Pickup)}
                    value={address.pickup ? address.pickup.zip : ""}
                    className={`${TripAddressStyle.customInput} ${uspsFieldValidationError.pickup && Style.errorBorder}`}
                  />
                </Col>
              </Row>
              <Row>
                <Col>
                  <span
                    onClick={() =>
                      handleClearAddressAndRest(
                        AddressType.Pickup,
                        !detailedViewAddress[AddressType.Pickup]
                      )
                    }
                    className={TripAddressStyle.contentTextBlueDimLight}
                  >
                    Reset Address Lookup
                  </span>
                </Col>
              </Row>
            </>
          )}
          {driverInstruction[AddressType.Pickup] && (
            <Row
              className={TripAddressStyle.addressLookupSection}
              align="middle"
            >
              <Col xl={{ span: 21 }} lg={{ span: 21 }}>
                <Row>
                  <Col xl={{ span: 15 }} lg={{ span: 15 }}>
                    <label className={TripAddressStyle.titleTextBoldBlue}>
                      Pickup Instructions&nbsp;
                      <Tooltip
                        title="The driver will be sent these instructions when arriving at the pickup address."
                        trigger="click"
                      >
                        <Image
                          preview={false}
                          width={"16px"}
                          src={DriverInstruction}
                        />
                      </Tooltip>
                    </label>
                  </Col>
                  <Col xl={{ offset: 6 }} lg={{ offset: 5 }}>
                    <span
                      onClick={() =>
                        handleInstructionView(
                          AddressType.Pickup,
                          !driverInstruction[AddressType.Pickup],
                          true
                        )
                      }
                      className={TripAddressStyle.contentTextBlueDimLight}
                    >
                      Remove
                    </span>
                  </Col>
                </Row>
                <Input.TextArea
                  autoSize={{ minRows: 1, maxRows: 2 }}
                  size="large"
                  name="driverInstruction"
                  onChange={(e) => changeFields(e, AddressType.Pickup)}
                  value={
                    address.pickup && address.pickup.driverInstruction
                      ? address.pickup.driverInstruction
                      : ""
                  }
                  className={TripAddressStyle.customInput}
                  placeholder="Enter instructions for the driver"
                />
                <Row justify="end">
                  <Typography.Text
                    className={
                      address.pickup &&
                      address.pickup.driverInstruction?.length ===
                        InputBoxLength.DriverInstructionText
                        ? TripAddressStyle.driverInstructionTextLengthRed
                        : TripAddressStyle.driverInstructionTextLength
                    }
                  >
                    {address.pickup && address.pickup.driverInstruction?.length
                      ? address.pickup.driverInstruction?.length
                      : 0}
                    /{InputBoxLength.DriverInstructionText}
                  </Typography.Text>
                </Row>
              </Col>
            </Row>
          )}
        </Col>
      </Row>
      <Row className={TripAddressStyle.addressSection}>
        <Col xl={{ span: 19 }} lg={{ span: 18 }}>
          <Row>
            <Col xl={{ span: 9 }} lg={{ span: 10 }}>
              <label className={TripAddressStyle.titleTextBoldBlue}>
                Dropoff Address
              </label>
            </Col>
            {!driverInstruction[AddressType.DropOff] && (
              <Col xl={{ push: 6 }} lg={{ push: 3 }}>
                <span
                  onClick={() =>
                    handleInstructionView(
                      AddressType.DropOff,
                      !driverInstruction[AddressType.DropOff]
                    )
                  }
                  className={TripAddressStyle.contentTextBlueDimLight}
                >
                  Add driver instructions
                </span>
              </Col>
            )}
          </Row>
          <Row align="middle">
            <Col span={21}>
              <Input
                size="large"
                autoComplete="off"
                className={TripAddressStyle.customInput}
                name={`${AddressType.DropOff}.address1`}
                onChange={(e) =>
                  handlePlaceAutoComplete(e, AddressType.DropOff)
                }
                value={
                  address.dropOff !== undefined
                    ? detailedViewAddress[AddressType.DropOff]
                      ? address.dropOff.address1
                      : AddressDisplay(address.dropOff)
                    : undefined
                }
                placeholder="Enter an address"
                prefix={
                  address.dropOff !== undefined &&
                  address.dropOff.isHomeAddress && (
                    <Image preview={false} src={HomeIcon} width={"15px"} />
                  )
                }
                suffix={
                  <CaretDownOutlined translate={""} style={{ color: "#FFF" }} />
                }
                ref={(input) => (inputPickupRefs.current.dropOff = input)}
              />
            </Col>
            {address[AddressType.DropOff]?.zip !== undefined && (
              <Col>
                <span
                  className={TripAddressStyle.contentTextBlueDimLightwithPl}
                  onClick={() =>
                    handleClearAddressAndRest(
                      AddressType.DropOff,
                      !detailedViewAddress[AddressType.DropOff]
                    )
                  }
                >
                  Clear
                </span>
              </Col>
            )}
          </Row>
          <Row>
            <Col>
              {sugg[AddressType.DropOff].length > 0 &&
                sugg[AddressType.DropOff].map((sug: any, i: number) => (
                  <span
                    key={i}
                    className={TripAddressStyle.makeClickable}
                    onClick={() =>
                      handlePlaceSelected(sug, AddressType.DropOff)
                    }
                  >
                    {sug.description}
                  </span>
                ))}
            </Col>
          </Row>
          {!detailedViewAddress.dropOff && (
            <Row className={TripAddressStyle.dropOffAddressLookupSection}>
              <Col>
                <span
                  onClick={() =>
                    handleDefaultAddressDetailView(
                      AddressType.DropOff,
                      !detailedViewAddress[AddressType.DropOff]
                    )
                  }
                  className={TripAddressStyle.contentTextBlueDimLight}
                >
                  Address lookup not working?
                </span>
              </Col>
            </Row>
          )}
          {detailedViewAddress[AddressType.DropOff] && (
            <>
              {" "}
              <Row className={TripAddressStyle.addressLookupSection}>
                <Col span={21}>
                  <Input
                    size="large"
                    type="text"
                    placeholder="Address 2"
                    name="address2"
                    onChange={(e) => changeFields(e, AddressType.DropOff)}
                    value={address.dropOff ? address.dropOff.address2 : ""}
                    className={TripAddressStyle.customInput}
                  />
                </Col>
              </Row>
              <Row className={TripAddressStyle.addressLookupSection}>
                <Col span={8}>
                  <Input
                    size="large"
                    type="text"
                    placeholder="City"
                    name="city"
                    onChange={(e) => changeFields(e, AddressType.DropOff)}
                    value={address.dropOff ? address.dropOff.city : ""}
                    className={`${TripAddressStyle.customInput} ${uspsFieldValidationError.dropOff && Style.errorBorder}`}
                  />
                </Col>
                <Col span={4} offset={1}>
                  <SearchSelect
                    placeholder="State"
                    options={states.map((r: any) => ({
                      key: r.id,
                      id: r.itemName,
                      display: r.itemName,
                    }))}
                    value={address.dropOff ? address.dropOff.state : ""}
                    className={`${TripAddressStyle.searchSelectSize} ${uspsFieldValidationError.dropOff && TripAddressStyle.errorOutline}`}
                  />
                </Col>
                <Col span={7} offset={1}>
                  <Input
                    size="large"
                    type="text"
                    placeholder="Zip Code"
                    name="zip"
                    onChange={(e) => changeFields(e, AddressType.DropOff)}
                    value={address.dropOff ? address.dropOff.zip : ""}
                    className={`${TripAddressStyle.customInput} ${uspsFieldValidationError.dropOff && Style.errorBorder}`}
                  />
                </Col>
              </Row>
              <Row>
                <Col>
                  <span
                    onClick={() =>
                      handleClearAddressAndRest(
                        AddressType.DropOff,
                        !detailedViewAddress[AddressType.DropOff]
                      )
                    }
                    className={TripAddressStyle.contentTextBlueDimLight}
                  >
                    Reset Address Lookup
                  </span>
                </Col>
              </Row>
            </>
          )}
          {driverInstruction[AddressType.DropOff] && (
            <Row className={TripAddressStyle.dropOffInstructions}>
              <Col xl={{ span: 21 }} lg={{ span: 21 }}>
                <Row>
                  <Col xl={{ span: 15 }} lg={{ span: 15 }}>
                    <label className={TripAddressStyle.titleTextBoldBlue}>
                      Dropoff Instructions&nbsp;
                      <Tooltip
                        title="The driver will be sent these instructions when arriving at the dropoff address."
                        trigger="click"
                      >
                        <Image
                          preview={false}
                          width={"16px"}
                          src={DriverInstruction}
                        />
                      </Tooltip>
                    </label>
                  </Col>
                  <Col xl={{ offset: 6 }} lg={{ offset: 5 }}>
                    <span
                      onClick={() =>
                        handleInstructionView(
                          AddressType.DropOff,
                          !driverInstruction[AddressType.DropOff],
                          true
                        )
                      }
                      className={TripAddressStyle.contentTextBlueDimLight}
                    >
                      Remove
                    </span>
                  </Col>
                </Row>
                <Input.TextArea
                  autoSize={{ minRows: 1, maxRows: 2 }}
                  size="large"
                  name="driverInstruction"
                  onChange={(e) => changeFields(e, AddressType.DropOff)}
                  value={
                    address.dropOff && address.dropOff.driverInstruction
                      ? address.dropOff.driverInstruction
                      : ""
                  }
                  className={TripAddressStyle.customInput}
                  placeholder="Enter instructions for the driver"
                />
                <Row justify="end">
                  <Typography.Text
                    className={
                      address.dropOff &&
                      address.dropOff.driverInstruction?.length ===
                        InputBoxLength.DriverInstructionText
                        ? TripAddressStyle.driverInstructionTextLengthRed
                        : TripAddressStyle.driverInstructionTextLength
                    }
                  >
                    {address.dropOff &&
                    address.dropOff.driverInstruction?.length
                      ? address.dropOff.driverInstruction?.length
                      : 0}
                    /{InputBoxLength.DriverInstructionText}
                  </Typography.Text>
                </Row>
              </Col>
            </Row>
          )}
        </Col>
      </Row>
    </>
  ) : (
    <>
      <Row className={TripAddressStyle.mobileAddressSection}>
        <Col span={24}>
          <Row>
            <Col span={12}>
              <label className={TripAddressStyle.titleTextBoldBlue}>
                Pickup Address
              </label>
            </Col>
            {!driverInstruction[AddressType.Pickup] && (
              <Col sm={{ span: 12, push: 5 }} xs={{ push: 1 }}>
                <span
                  onClick={() =>
                    handleInstructionView(
                      AddressType.Pickup,
                      !driverInstruction[AddressType.Pickup]
                    )
                  }
                  className={TripAddressStyle.contentTextBlueDimLight}
                >
                  Add driver instructions
                </span>
              </Col>
            )}
          </Row>
          <Row>
            <Col span={22}>
              <Input
                size="large"
                autoComplete="off"
                className={TripAddressStyle.customInput}
                name={`${AddressType.Pickup}.address1`}
                onChange={(e) => handlePlaceAutoComplete(e, AddressType.Pickup)}
                value={
                  address.pickup !== undefined
                    ? detailedViewAddress[AddressType.Pickup]
                      ? address.pickup.address1
                      : AddressDisplay(address.pickup)
                    : undefined
                }
                placeholder="Enter an address"
                prefix={
                  address.pickup !== undefined &&
                  address.pickup.isHomeAddress && (
                    <Image preview={false} src={HomeIcon} width={"15px"} />
                  )
                }
              />
            </Col>
          </Row>
          <Row>
            <Col>
              {sugg[AddressType.Pickup].length > 0 &&
                sugg[AddressType.Pickup].map((sug: any, i: number) => (
                  <span
                    key={i}
                    className={TripAddressStyle.makeClickable}
                    onClick={() =>
                      handlePlaceSelected(sug, AddressType.Pickup)
                    }
                  >
                    {sug.description}
                  </span>
                ))}
            </Col>
          </Row>
          <Row className={TripAddressStyle.pickupAddressLookupSection}>
            <Col span={12}>
              <span
                onClick={() =>
                  handleDefaultAddressDetailView(
                    AddressType.Pickup,
                    !detailedViewAddress[AddressType.Pickup]
                  )
                }
                className={TripAddressStyle.contentTextBlueDimLight}
              >
                Address lookup not working?
              </span>
            </Col>
            <Col span={12} push={8}>
              {address.pickup !== undefined && (
                <Col>
                  <span
                    className={
                      TripAddressStyle.contentTextBlueDimLightwithPlMobile
                    }
                    onClick={() => handleClear(AddressType.Pickup)}
                  >
                    Clear
                  </span>
                </Col>
              )}
            </Col>
          </Row>
          {detailedViewAddress[AddressType.Pickup] && (
            <>
              {" "}
              <Row className={TripAddressStyle.addressLookupSection}>
                <Col span={22}>
                  <Input
                    size="large"
                    type="text"
                    placeholder="Address 2"
                    name="address2"
                    onChange={(e) => changeFields(e, AddressType.Pickup)}
                    value={address.pickup ? address.pickup.address2 : ""}
                    className={TripAddressStyle.customInput}
                  />
                </Col>
              </Row>
              <Row className={TripAddressStyle.addressLookupSection}>
                <Col span={8}>
                  <Input
                    size="large"
                    type="text"
                    placeholder="City"
                    name="city"
                    onChange={(e) => changeFields(e, AddressType.Pickup)}
                    value={address.pickup ? address.pickup.city : ""}
                    className={`${TripAddressStyle.customInput} ${uspsFieldValidationError.pickup && Style.errorBorder}`}
                  />
                </Col>
                <Col span={4} offset={1}>
                  <SearchSelect
                    placeholder="State"
                    options={states.map((r: any) => ({
                      key: r.id,
                      id: r.itemName,
                      display: r.itemName,
                    }))}
                    value={address.pickup ? address.pickup.state : ""}
                    className={`${TripAddressStyle.searchSelectSize} ${uspsFieldValidationError.pickup && TripAddressStyle.errorOutline}`}
                  />
                </Col>
                <Col span={8} offset={1}>
                  <Input
                    size="large"
                    type="text"
                    placeholder="Zip Code"
                    name="zip"
                    onChange={(e) => changeFields(e, AddressType.Pickup)}
                    value={address.pickup ? address.pickup.zip : ""}
                    className={`${TripAddressStyle.customInput} ${uspsFieldValidationError.pickup && Style.errorBorder}`}
                  />
                </Col>
              </Row>
              <Row>
                <Col>
                  <span
                    onClick={() => handleClear(AddressType.Pickup)}
                    className={TripAddressStyle.contentTextBlueDimLight}
                  >
                    Reset Address Lookup
                  </span>
                </Col>
              </Row>
            </>
          )}
          {driverInstruction[AddressType.Pickup] && (
            <Row
              className={TripAddressStyle.addressLookupSection}
              align="middle"
            >
              <Col span={22}>
                <Row>
                  <Col span={14}>
                    <label className={TripAddressStyle.titleTextBoldBlue}>
                      Pickup Instructions&nbsp;
                      <Tooltip
                        title="The driver will be sent these instructions when arriving at the pickup address."
                        trigger="click"
                      >
                        <Image
                          preview={false}
                          width={"16px"}
                          src={DriverInstruction}
                        />
                      </Tooltip>
                    </label>
                  </Col>
                  <Col span={10} push={6}>
                    <span
                      onClick={() =>
                        handleInstructionView(
                          AddressType.Pickup,
                          !driverInstruction[AddressType.Pickup],
                          true
                        )
                      }
                      className={TripAddressStyle.contentTextBlueDimLight}
                    >
                      Remove
                    </span>
                  </Col>
                </Row>
                <Input.TextArea
                  autoSize={{ minRows: 1, maxRows: 2 }}
                  size="large"
                  name="driverInstruction"
                  onChange={(e) => changeFields(e, AddressType.Pickup)}
                  value={
                    address.pickup && address.pickup.driverInstruction
                      ? address.pickup.driverInstruction
                      : ""
                  }
                  className={TripAddressStyle.customInput}
                  placeholder="Enter instructions for the driver"
                />
                <Row justify="end">
                  <Typography.Text
                    className={
                      address.pickup &&
                      address.pickup.driverInstruction?.length ===
                        InputBoxLength.DriverInstructionText
                        ? TripAddressStyle.driverInstructionTextLengthRed
                        : TripAddressStyle.driverInstructionTextLength
                    }
                  >
                    {address.pickup && address.pickup.driverInstruction?.length
                      ? address.pickup.driverInstruction?.length
                      : 0}
                    /{InputBoxLength.DriverInstructionText}
                  </Typography.Text>
                </Row>
              </Col>
            </Row>
          )}
        </Col>
      </Row>
      <Row className={TripAddressStyle.addressSection}>
        <Col span={24}>
          <Row>
            <Col span={12}>
              <label className={TripAddressStyle.titleTextBoldBlue}>
                Dropoff Address
              </label>
            </Col>
            {!driverInstruction[AddressType.DropOff] && (
              <Col sm={{ span: 12, push: 5 }} xs={{ push: 1 }}>
                <span
                  onClick={() =>
                    handleInstructionView(
                      AddressType.DropOff,
                      !driverInstruction[AddressType.DropOff]
                    )
                  }
                  className={TripAddressStyle.contentTextBlueDimLight}
                >
                  Add driver instructions
                </span>
              </Col>
            )}
          </Row>
          <Row>
            <Col span={22}>
              <Input
                size="large"
                autoComplete="off"
                className={TripAddressStyle.customInput}
                name={`${AddressType.DropOff}.address1`}
                onChange={(e) =>
                  handlePlaceAutoComplete(e, AddressType.DropOff)
                }
                value={
                  address.dropOff !== undefined
                    ? detailedViewAddress[AddressType.DropOff]
                      ? address.dropOff.address1
                      : AddressDisplay(address.dropOff)
                    : undefined
                }
                placeholder="Enter an address"
                prefix={
                  address.dropOff !== undefined &&
                  address.dropOff.isHomeAddress && (
                    <Image preview={false} src={HomeIcon} width={"15px"} />
                  )
                }
              />
            </Col>
          </Row>
          <Row>
            <Col>
              {sugg[AddressType.DropOff].length > 0 &&
                sugg[AddressType.DropOff].map((sug: any, i: number) => (
                  <span
                    key={i}
                    className={TripAddressStyle.makeClickable}
                    onClick={() =>
                      handlePlaceSelected(sug, AddressType.DropOff)
                    }
                  >
                    {sug.description}
                  </span>
                ))}
            </Col>
          </Row>
          <Row className={TripAddressStyle.dropOffAddressLookupSection}>
            <Col span={12}>
              <span
                onClick={() =>
                  handleDefaultAddressDetailView(
                    AddressType.DropOff,
                    !detailedViewAddress[AddressType.DropOff]
                  )
                }
                className={TripAddressStyle.contentTextBlueDimLight}
              >
                Address lookup not working?
              </span>
            </Col>
            <Col span={12} push={8}>
              {address[AddressType.DropOff] !== undefined && (
                <Col>
                  <span
                    className={
                      TripAddressStyle.contentTextBlueDimLightwithPlMobile
                    }
                    onClick={() => handleClear(AddressType.DropOff)}
                  >
                    Clear
                  </span>
                </Col>
              )}
            </Col>
          </Row>
          {detailedViewAddress[AddressType.DropOff] && (
            <>
              {" "}
              <Row className={TripAddressStyle.addressLookupSection}>
                <Col span={22}>
                  <Input
                    size="large"
                    type="text"
                    placeholder="Address 2"
                    name="address2"
                    onChange={(e) => changeFields(e, AddressType.DropOff)}
                    value={address.dropOff ? address.dropOff.address2 : ""}
                    className={TripAddressStyle.customInput}
                  />
                </Col>
              </Row>
              <Row className={TripAddressStyle.addressLookupSection}>
                <Col span={8}>
                  <Input
                    size="large"
                    type="text"
                    placeholder="City"
                    name="city"
                    onChange={(e) => changeFields(e, AddressType.DropOff)}
                    value={address.dropOff ? address.dropOff.city : ""}
                    className={`${TripAddressStyle.customInput} ${uspsFieldValidationError.dropOff && Style.errorBorder}`}
                  />
                </Col>
                <Col span={4} offset={1}>
                  <SearchSelect
                    placeholder="State"
                    options={states.map((r: any) => ({
                      key: r.id,
                      id: r.itemName,
                      display: r.itemName,
                    }))}
                    value={address.dropOff ? address.dropOff.state : ""}
                    className={`${TripAddressStyle.searchSelectSize} ${uspsFieldValidationError.dropOff && TripAddressStyle.errorOutline}`}
                  />
                </Col>
                <Col span={8} offset={1}>
                  <Input
                    size="large"
                    type="text"
                    placeholder="Zip Code"
                    name="zip"
                    onChange={(e) => changeFields(e, AddressType.DropOff)}
                    value={address.dropOff ? address.dropOff.zip : ""}
                    className={`${TripAddressStyle.customInput} ${uspsFieldValidationError.dropOff && Style.errorBorder}`}
                  />
                </Col>
              </Row>
              <Row>
                <Col>
                  <span
                    onClick={() => handleClear(AddressType.DropOff)}
                    className={TripAddressStyle.contentTextBlueDimLight}
                  >
                    Reset Address Lookup
                  </span>
                </Col>
              </Row>
            </>
          )}
          {driverInstruction[AddressType.DropOff] && (
            <Row className={TripAddressStyle.addressLookupSection}>
              <Col span={22}>
                <Row>
                  <Col span={14}>
                    <label className={TripAddressStyle.titleTextBoldBlue}>
                      Dropoff Instructions&nbsp;
                      <Tooltip
                        title="The driver will be sent these instructions when arriving at the dropoff address."
                        trigger="click"
                      >
                        <Image
                          preview={false}
                          width={"16px"}
                          src={DriverInstruction}
                        />
                      </Tooltip>
                    </label>
                  </Col>
                  <Col span={10} push={6}>
                    <span
                      onClick={() =>
                        handleInstructionView(
                          AddressType.DropOff,
                          !driverInstruction[AddressType.DropOff],
                          true
                        )
                      }
                      className={TripAddressStyle.contentTextBlueDimLight}
                    >
                      Remove
                    </span>
                  </Col>
                </Row>
                <Input.TextArea
                  autoSize={{ minRows: 1, maxRows: 2 }}
                  size="large"
                  name="driverInstruction"
                  onChange={(e) => changeFields(e, AddressType.DropOff)}
                  value={
                    address.dropOff && address.dropOff.driverInstruction
                      ? address.dropOff.driverInstruction
                      : ""
                  }
                  className={TripAddressStyle.customInput}
                  placeholder="Enter instructions for the driver"
                />
                <Row justify="end">
                  <Typography.Text
                    className={
                      address.dropOff &&
                      address.dropOff.driverInstruction?.length ===
                        InputBoxLength.DriverInstructionText
                        ? TripAddressStyle.driverInstructionTextLengthRed
                        : TripAddressStyle.driverInstructionTextLength
                    }
                  >
                    {address.dropOff &&
                    address.dropOff.driverInstruction?.length
                      ? address.dropOff.driverInstruction?.length
                      : 0}
                    /{InputBoxLength.DriverInstructionText}
                  </Typography.Text>
                </Row>
              </Col>
            </Row>
          )}
        </Col>
      </Row>
    </>
  );
};

export default TripAddress;
